import type { ErrorEvent, Exception } from '@sentry/nextjs'
import { classifyRealtimeCdcTransientError } from '@/lib/salesforce/realtime-backoff'

const HOURLY_TRANSIENT_CDC_EVENT_KEYS = new Map<string, number>()
const ONE_HOUR_MS = 60 * 60 * 1000

export type RealtimeCdcSuppressionScope = {
  kind: NonNullable<ReturnType<typeof classifyRealtimeCdcTransientError>>
  tenantId: string
  sobjectName: string
}

function stackFunctionsText(value: Exception): string {
  const functions = value.stacktrace?.frames
    ?.map((frame) => frame.function)
    .filter(Boolean)

  return functions?.join(' ') ?? ''
}

function eventText(event: ErrorEvent): string {
  const exceptionText = event.exception?.values
    ?.map((value) => {
      const parts = [value.type, value.value, stackFunctionsText(value)].filter(Boolean)
      return parts.join(' ')
    })
    .join(' ') ?? ''

  return [event.message, exceptionText, JSON.stringify(event.tags ?? {}), JSON.stringify(event.extra ?? {})]
    .filter(Boolean)
    .join(' ')
}

export function getRealtimeCdcSuppressionScope(event: ErrorEvent): RealtimeCdcSuppressionScope | null {
  const text = eventText(event)
  const kind = classifyRealtimeCdcTransientError(new Error(text))
  if (!kind) return null

  const fromRealtimePath = /runRealtimeSyncForConfig|subscribeFromReplay|subscribeLatest|pubsub-stream|realtime_cdc|cron_realtime_sync_error|salesforce-sync/.test(text)
  const fromEntityUpsertPath = kind === 'entity_upsert_error' && /entity_upsert_error|bulk_upsert_entities|salesforce-sync/.test(text)
  if (!fromRealtimePath && !fromEntityUpsertPath) return null

  return {
    kind,
    tenantId: String(event.tags?.tenant_id ?? event.extra?.tenantId ?? event.extra?.tenant_id ?? 'unknown-tenant'),
    sobjectName: String(event.extra?.sobjectName ?? event.extra?.sobject_name ?? event.contexts?.salesforce_pubsub?.sobjectName ?? 'unknown-sobject'),
  }
}

function realtimeTransientKey(event: ErrorEvent): string | null {
  const scope = getRealtimeCdcSuppressionScope(event)
  if (!scope) return null

  return `${scope.kind}:${scope.tenantId}:${scope.sobjectName}`
}

export function shouldDropRealtimeTransientCdcEvent(event: ErrorEvent, now = Date.now()): boolean {
  const key = realtimeTransientKey(event)
  if (!key) return false

  const lastAllowedAt = HOURLY_TRANSIENT_CDC_EVENT_KEYS.get(key)
  if (lastAllowedAt === undefined || now - lastAllowedAt >= ONE_HOUR_MS) {
    HOURLY_TRANSIENT_CDC_EVENT_KEYS.set(key, now)
    return false
  }

  return true
}

export function resetRealtimeTransientCdcEventSuppressionForTest(): void {
  HOURLY_TRANSIENT_CDC_EVENT_KEYS.clear()
}
